import { useEffect, useRef } from 'react';
import { useReactFlow, useViewport } from '@xyflow/react';
import { useNodeStore } from '@/stores/node-store';
import { useNodeExplorerStore } from '@/stores/node-explorer-store';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import {
  DEFAULT_NODE_WIDTH,
  DEFAULT_NODE_HEIGHT,
} from '@/constants/workflow-editor';

// Constants for auto-pan visibility calculation
const VISIBILITY_PADDING = 20; // Small padding in pixels from edges
const TOP_PADDING_FOR_CONTROL_BAR = 60; // Extra space at top for floating control bar (positioned at -40px above node)
const MIN_OVERLAP_THRESHOLD = 10; // Only pan if node is at least this many pixels outside visible area

interface UseKeepSelectedNodeVisibleProps {
  panelGroupWidth: number;
}

/**
 * Hook that automatically pans the viewport to keep the selected node visible.
 *
 * Handles:
 * - Left sidebar (Node Explorer) - pans when node's left edge is behind it
 * - Right sidebar (Console) - pans when node's right edge is behind it
 * - Viewport edges - pans when node is outside the visible canvas area (top/bottom/left/right)
 */
export function useKeepSelectedNodeVisible({
  panelGroupWidth,
}: UseKeepSelectedNodeVisibleProps) {
  const { flowToScreenPosition, getNode, getViewport, setViewport } =
    useReactFlow();
  const { zoom } = useViewport();
  const { lastSelectedNodeId } = useNodeStore();

  // Left sidebar (Node Explorer) state
  const {
    isMaximized: isLeftSidebarMaximized,
    isHoveringWhileMinimized: isLeftSidebarHovering,
    panelSize: leftSidebarPanelSize,
  } = useNodeExplorerStore();

  // Right sidebar (Console) state
  const {
    isMaximized: isRightSidebarMaximized,
    isHoveringWhileMinimized: isRightSidebarHovering,
    isConsolePanelOpen,
    panelSize: rightSidebarPanelSize,
  } = useWorkflowPanelStore();

  // Calculate sidebar visibility
  const isLeftSidebarVisible = isLeftSidebarMaximized || isLeftSidebarHovering;
  const isRightSidebarVisible =
    isRightSidebarMaximized || isRightSidebarHovering || isConsolePanelOpen;

  // Get focused node ID (last clicked node from store)
  const focusedNodeId = lastSelectedNodeId;

  // Track which checks we've already handled for the current node
  // This prevents infinite loops while still allowing pans when sidebars change
  const handledStateRef = useRef<{
    nodeId: string | null;
    leftSidebarHandled: boolean;
    rightSidebarHandled: boolean;
    viewportHandled: boolean; // Track if we've done the initial viewport check
  }>({
    nodeId: null,
    leftSidebarHandled: false,
    rightSidebarHandled: false,
    viewportHandled: false,
  });

  useEffect(() => {
    // Reset tracking when node changes
    if (handledStateRef.current.nodeId !== focusedNodeId) {
      handledStateRef.current = {
        nodeId: focusedNodeId,
        leftSidebarHandled: false,
        rightSidebarHandled: false,
        viewportHandled: false,
      };
    }

    // Skip if no node is focused
    if (!focusedNodeId) {
      return;
    }

    // Determine which checks need to be performed
    const shouldCheckLeft =
      isLeftSidebarVisible && !handledStateRef.current.leftSidebarHandled;
    const shouldCheckRight =
      isRightSidebarVisible && !handledStateRef.current.rightSidebarHandled;
    const shouldCheckViewport = !handledStateRef.current.viewportHandled;

    // Skip if all checks have been handled
    if (!shouldCheckLeft && !shouldCheckRight && !shouldCheckViewport) {
      return;
    }

    // Get node with measured dimensions from React Flow
    const node = getNode(focusedNodeId);
    if (!node) return;

    // Prefer DOM bounds: grouped children use parent-relative positions in the
    // store, and measured sizes may omit expanded children or overflow content.
    const nodeElement = document.querySelector(`[data-id="${focusedNodeId}"]`);
    const nodeRect = nodeElement?.getBoundingClientRect();
    const nodeWidth =
      nodeRect?.width ?? node.measured?.width ?? DEFAULT_NODE_WIDTH;
    const nodeHeight =
      nodeRect?.height ?? node.measured?.height ?? DEFAULT_NODE_HEIGHT;

    // Get the React Flow container's position and size on screen
    // This accounts for any offset from the main navigation sidebar
    const reactFlowContainer = document.querySelector('.react-flow');
    if (!reactFlowContainer) return;
    const containerRect = reactFlowContainer.getBoundingClientRect();

    // Calculate sidebar widths (only for visible sidebars)
    const leftSidebarWidth = isLeftSidebarVisible ? leftSidebarPanelSize : 0;
    const rightSidebarWidth = isRightSidebarVisible ? rightSidebarPanelSize : 0;

    let nodeLeftEdge: number;
    let nodeRightEdge: number;
    let nodeTopEdge: number;
    let nodeBottomEdge: number;

    if (nodeRect && nodeRect.width > 0 && nodeRect.height > 0) {
      nodeLeftEdge = nodeRect.left;
      nodeRightEdge = nodeRect.right;
      nodeTopEdge = nodeRect.top;
      nodeBottomEdge = nodeRect.bottom;
    } else {
      const screenPos = flowToScreenPosition({
        x: node.position.x,
        y: node.position.y,
      });
      nodeLeftEdge = screenPos.x;
      nodeRightEdge = screenPos.x + nodeWidth;
      nodeTopEdge = screenPos.y;
      nodeBottomEdge = screenPos.y + nodeHeight;
    }

    // Calculate visible boundaries in screen coordinates
    // Horizontal: account for sidebars and container offset
    // Use containerRect.right (actual canvas edge) instead of panelGroupWidth
    // because panelGroupWidth includes the Past Runs sidebar, but the ReactFlow
    // canvas only occupies the flex-1 portion (narrower when Past Runs is open)
    const visibleLeftBoundary =
      containerRect.left + leftSidebarWidth + VISIBILITY_PADDING;
    const visibleRightBoundary =
      containerRect.right - rightSidebarWidth - VISIBILITY_PADDING;
    // Vertical: account for container position and size
    // Top needs extra padding for the floating control bar above the node
    const visibleTopBoundary =
      containerRect.top + VISIBILITY_PADDING + TOP_PADDING_FOR_CONTROL_BAR;
    const visibleBottomBoundary =
      containerRect.top + containerRect.height - VISIBILITY_PADDING;

    // Calculate how much the node extends outside each boundary
    const leftOverlap = visibleLeftBoundary - nodeLeftEdge; // Positive if outside left
    const rightOverlap = nodeRightEdge - visibleRightBoundary; // Positive if outside right
    const topOverlap = visibleTopBoundary - nodeTopEdge; // Positive if outside top
    const bottomOverlap = nodeBottomEdge - visibleBottomBoundary; // Positive if outside bottom

    // Only consider "outside" if overlap exceeds threshold
    const isOutsideLeft = leftOverlap > MIN_OVERLAP_THRESHOLD;
    const isOutsideRight = rightOverlap > MIN_OVERLAP_THRESHOLD;
    const isOutsideTop = topOverlap > MIN_OVERLAP_THRESHOLD;
    const isOutsideBottom = bottomOverlap > MIN_OVERLAP_THRESHOLD;

    // Calculate offsets needed
    let offsetX = 0;
    let offsetY = 0;

    // Check horizontal visibility (prioritize sidebars, then edges)
    if (shouldCheckLeft && isOutsideLeft) {
      offsetX = leftOverlap;
      handledStateRef.current.leftSidebarHandled = true;
    } else if (shouldCheckRight && isOutsideRight) {
      offsetX = -rightOverlap;
      handledStateRef.current.rightSidebarHandled = true;
    } else {
      // Node is horizontally visible, mark sidebars as handled
      if (shouldCheckLeft) {
        handledStateRef.current.leftSidebarHandled = true;
      }
      if (shouldCheckRight) {
        handledStateRef.current.rightSidebarHandled = true;
      }
    }

    // Check vertical visibility (independent of sidebar state)
    if (shouldCheckViewport) {
      if (isOutsideTop) {
        offsetY = topOverlap;
      } else if (isOutsideBottom) {
        offsetY = -bottomOverlap;
      }
      // Also check horizontal edges if sidebars aren't covering them
      if (offsetX === 0) {
        if (isOutsideLeft) {
          offsetX = leftOverlap;
        } else if (isOutsideRight) {
          offsetX = -rightOverlap;
        }
      }
      handledStateRef.current.viewportHandled = true;
    }

    // Reset handled state when sidebar closes (so next open will trigger check)
    if (!isLeftSidebarVisible) {
      handledStateRef.current.leftSidebarHandled = false;
    }
    if (!isRightSidebarVisible) {
      handledStateRef.current.rightSidebarHandled = false;
    }

    // Pan if needed (horizontal, vertical, or both)
    if (offsetX !== 0 || offsetY !== 0) {
      const viewport = getViewport();
      setViewport(
        {
          x: viewport.x + offsetX,
          y: viewport.y + offsetY,
          zoom: viewport.zoom,
        },
        { duration: 300 },
      );
    }
  }, [
    focusedNodeId,
    isLeftSidebarVisible,
    isRightSidebarVisible,
    leftSidebarPanelSize,
    rightSidebarPanelSize,
    panelGroupWidth,
    flowToScreenPosition,
    setViewport,
    getViewport,
    getNode,
    zoom,
  ]);
}
